/**
 * 62.59 (lane F; `sweeps/2026-10-11-spread-vs-dense.md`): the exact horizon cull on a person's line of sight.
 *
 * In clear weather a bot's look casts a line to every living enemy on the planet, whatever the range, and a line to someone
 * over the horizon still marches until it meets the ground (core/los.ts `hasLineOfSight` → core/surface.ts
 * `raycastTerrain`): in a battle spread over a planet, half the pairs. A line that runs below the planet's **lowest
 * ground** for longer than one march step is blocked whatever the march finds — one of the march's samples (a lattice
 * RAY_MARCH_STEP apart from where the ray enters the planet's shell, to the line's end less LOS_END_EPSILON) lies in that
 * stretch, and every point there is inside the ground. So the line is answered blocked without the march: the same answer
 * (the march only says whether, and its scratch hints never change an answer). `tests/horizonCull.test.ts` holds every
 * culled line against the march.
 *
 * **The lowest ground.** The march's ground in a direction is the displaced mesh: a point on a flat triangle between three
 * displaced vertices (give or take BARY_EPS at an edge), or a vertex's own radius where the lookup falls back. A point of a
 * flat triangle is a convex combination of its corners, so its length is at least its projection on the triangle's centre
 * direction: at least the lowest corner's radius × the cosine of the triangle's angular radius. Over the planet: the
 * lowest displaced vertex × the narrowest such cosine, less 2e-5 of it for the edge tolerance. Memoised per heightmap (a
 * crater makes a new one, copy-on-write) and per mesh. A planet with no mesh to read gets no cull.
 *
 * The person path only (fine ground, bots): the kernel's own line of sight (lib.rs) and its TypeScript twin are untouched
 * (decisions 42, 43). Pure arithmetic and `Math.sqrt`: the same on every engine.
 */
import type { PlanetState, Vec3 } from '../types';
import { getTerrainIndex, RAY_MARCH_STEP, type TerrainIndex } from './surface';
import { LOS_END_EPSILON } from './los';

/** Per mesh (basePositions): the narrowest cosine of a triangle's angular radius about its centre direction (1: no triangles). */
const meshCos = new WeakMap<Float32Array, number>();
/** Per heightmap: the lowest ground's radius (0: no cull), with the mesh and radius it was taken on. */
const lowest = new WeakMap<Float32Array, { bp: Float32Array; radius: number; low: number }>();

function narrowestCos(bp: Float32Array, idx: TerrainIndex): number {
  const kept = meshCos.get(bp);
  if (kept !== undefined) return kept;
  let cos = 1;
  if (idx.valid) {
    const inv = idx.invLen;
    for (let t = 0; t < idx.triCount; t++) {
      const i = t * 3;
      const ax = bp[i * 3] * inv[i], ay = bp[i * 3 + 1] * inv[i], az = bp[i * 3 + 2] * inv[i];
      const bx = bp[i * 3 + 3] * inv[i + 1], by = bp[i * 3 + 4] * inv[i + 1], bz = bp[i * 3 + 5] * inv[i + 1];
      const cx = bp[i * 3 + 6] * inv[i + 2], cy = bp[i * 3 + 7] * inv[i + 2], cz = bp[i * 3 + 8] * inv[i + 2];
      const mx = ax + bx + cx, my = ay + by + cy, mz = az + bz + cz, ml = Math.sqrt(mx * mx + my * my + mz * mz);
      if (!(ml > 0)) { cos = 0; break; }
      const c = Math.min(ax * mx + ay * my + az * mz, bx * mx + by * my + bz * mz, cx * mx + cy * my + cz * mz) / ml;
      if (c < cos) cos = c;
    }
  }
  meshCos.set(bp, cos);
  return cos;
}

/** The radius below which every direction's ground (the march's `getSurfaceRadiusRaw`) stands on `planet`; 0 when unknown. */
export function lowestGround(planet: Pick<PlanetState, 'heightmap' | 'basePositions' | 'radius'>): number {
  const hm = planet.heightmap, bp = planet.basePositions;
  const kept = lowest.get(hm);
  if (kept && kept.bp === bp && kept.radius === planet.radius) return kept.low;
  let low = 0;
  if (hm.length > 0 && bp.length === hm.length * 3) {
    const idx = getTerrainIndex(bp), inv = idx.invLen;
    let rmin = Infinity;
    for (let i = 0; i < hm.length; i++) {
      // a displaced vertex (base × (1 + h / |base|), as the lookup builds it) and the lookup's fallback (radius + h)
      const x = bp[i * 3], y = bp[i * 3 + 1], z = bp[i * 3 + 2];
      const r = Math.min(Math.sqrt(x * x + y * y + z * z) * (1 + hm[i] * inv[i]), planet.radius + hm[i]);
      if (!(r >= 0)) { rmin = NaN; break; }   // (no cull on ground the lookup cannot read)
      if (r < rmin) rmin = r;
    }
    const cos = narrowestCos(bp, idx);
    if (rmin > 0 && Number.isFinite(rmin) && cos > 0) low = rmin * cos * (1 - 2e-5);
  }
  lowest.set(hm, { bp, radius: planet.radius, low });
  return low;
}

function lexLess(a: Vec3, b: Vec3): boolean {
  if (a.x !== b.x) return a.x < b.x;
  if (a.y !== b.y) return a.y < b.y;
  return a.z < b.z;
}

/**
 * Is the line a→b blocked by a planet's ground for certain — below its lowest ground for longer than one march step,
 * between where the march starts and where it stops (`hasLineOfSight`'s ray: from the lexicographically smaller end, to
 * the other less LOS_END_EPSILON)? True means `hasLineOfSight(a, b)` is false; false means nothing (march it).
 */
export function lineUnderGround(a: Vec3, b: Vec3, planets: readonly PlanetState[]): boolean {
  const from = lexLess(a, b) ? a : b, to = from === a ? b : a;
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const maxDist = dist - LOS_END_EPSILON;
  if (!(maxDist > RAY_MARCH_STEP)) return false;
  const ux = dx / dist, uy = dy / dist, uz = dz / dist;
  for (const planet of planets) {
    if (!planet.alive) continue;
    const m = lowestGround(planet);
    if (!(m > 0)) continue;
    const ox = from.x - planet.position.x, oy = from.y - planet.position.y, oz = from.z - planet.position.z;
    const b = ox * ux + oy * uy + oz * uz;
    const disc = b * b - (ox * ox + oy * oy + oz * oz - m * m);
    if (!(disc > 0)) continue;
    const sq = Math.sqrt(disc);
    // the stretch under the lowest ground, within the march's reach (its shell entry is never after this stretch starts)
    const lo = Math.max(-b - sq, 0), hi = Math.min(-b + sq, maxDist);
    if (hi - lo > RAY_MARCH_STEP * (1 + 1e-6) + 1e-9 * planet.radius) return true;
  }
  return false;
}
